import React, { useEffect, ReactNode } from 'react';
import { compose } from 'recompose';
import { useRouter } from 'next/router';
import buildUrl from 'lib/url';
import withInviter, {
  LOAD_INVITER_ERROR_TYPE,
  WithInviterProps,
} from 'data/inviter/withInviter';
import withUserData, { WithUserDataProps } from 'data/user/withUserData';
import withPlan, { WithPlanProps } from 'data/plan/withPlan';
import { trackEvent, withTrackLinkEvent } from 'lib/tracking';
import withErrorHandler from 'data/error/withErrorHandler';
import GeneralError from 'containers/Error/GeneralError';
import LoadingScreen from 'components/Loading/Screen';
import {
  joinLandingPageAuth,
  joinLandingPageNonAuth,
} from 'lib/trackingEvents';
import { InviteToken } from 'types';
import { useMasthead } from 'data/masthead/useMasthead';
import { Locale } from 'lib/i18n';

type TrackPageViewProps = {
  onPageView: () => void;
  children: ReactNode;
};

const TrackPageView = ({ onPageView, children }: TrackPageViewProps) => {
  useEffect(() => {
    onPageView();
  }, [onPageView]);
  return <>{children}</>;
};

type WithInviterLoadingProps = WithInviterProps &
  WithPlanProps &
  WithUserDataProps & {
    inviteToken: InviteToken;
  };

export function withInviterLoading(SubLandingPageComponent: any) {
  return ({
    inviteToken,
    isLoadingInviter,
    error,
    errors = [],
    themes,
    ...props
  }: WithInviterLoadingProps) => {
    const router = useRouter();
    const mastheadProps = useMasthead(router.locale as Locale);

    // const isUserEndpointError = hasErrorOfType(errors, LOAD_USER_DATA_ERROR_TYPE);
    const inviterError = errors.find(
      e => e?.type === LOAD_INVITER_ERROR_TYPE && e?.status !== 401,
    );

    const shouldDisplayGeneralError = typeof inviterError !== 'undefined';

    if (shouldDisplayGeneralError) {
      return <GeneralError error={inviterError} />;
    }

    if (isLoadingInviter || mastheadProps.isLoading) {
      return <LoadingScreen theme={themes.light} />;
    }

    const isAnonymous = !props.userData;
    const senderLogOptions = isAnonymous
      ? { authorize: false, flush: true }
      : {};
    const event = isAnonymous ? joinLandingPageNonAuth : joinLandingPageAuth;

    return (
      <TrackPageView
        onPageView={() => trackEvent(event.getPageView(), senderLogOptions)}
      >
        <SubLandingPageComponent
          theme={themes.legacyDark || themes.dark}
          joinUrl={buildUrl(props, 'SUB_CONFIRM_ACCOUNT', {
            token: inviteToken,
          })}
          termsUrl={buildUrl(props, 'LEGAL_TERMS')}
          onJoinClick={withTrackLinkEvent(event.getJoin(), senderLogOptions)}
          onTermsClick={withTrackLinkEvent(event.getToc(), senderLogOptions)}
          mastheadData={mastheadProps.data}
        />
      </TrackPageView>
    );
  };
}

export function getSubLandingPageContainer(Component: any) {
  return compose(
    withPlan,
    withErrorHandler,
    withInviter({ showUserSpecificErrors: false, showErrorOnNoInvite: false }),
    withUserData,
    withInviterLoading,
  )(Component);
}
